import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from "@angular/core";
import { Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { HeicConverterService } from "src/app/shared/services/heic-converter.service";
import { MultipartS3UploadService } from "src/app/shared/services/multipart-s3-upload.service";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import { VideoThumbnailService } from "src/app/shared/services/video-thumbnail.service";

@Component({
  selector: "app-tab-brand-materials",
  templateUrl: "./tab-brand-materials.component.html",
  styleUrls: ["./tab-brand-materials.component.scss"],
})
export class TabBrandMaterialsComponent implements OnInit, OnDestroy {
  private readonly VIDEO_UPLOAD_MAX_RETRIES = 3;
  private readonly VIDEO_UPLOAD_RETRY_DELAY_MS = 1200;
  private readonly FILE_UPLOAD_MAX_RETRIES = 3;
  private readonly FILE_UPLOAD_RETRY_DELAY_MS = 1200;

  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() fonts: string[] = [];
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();

  brandLogoFile: File;
  inspirationalVideoFile: File;
  private failedBrandLogoFile: File | null = null;
  private failedBannerFile: File | null = null;
  private failedInspirationalVideoFile: File | null = null;
  brandLogoUploadError: string | null = null;
  bannerUploadError: string | null = null;
  inspirationalVideoUploadError: string | null = null;

  // Track active upload subscriptions for cancellation
  private activeUploads: Map<string, { subscription: Subscription }> = new Map();
  private thumbnailFromUrlInFlight: Set<string> = new Set();
  private visibilityChangeHandler: (() => void) | null = null;
  private windowFocusHandler: (() => void) | null = null;

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private multipartUploadService: MultipartS3UploadService,
    private renderer: Renderer2,
    private uploadThumbnailService: VideoThumbnailService,
    private heicConverter: HeicConverterService
  ) {}

  ngOnInit(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === "visible") {
        this.retryInspirationalThumbnailIfMissing();
      }
    };
    this.windowFocusHandler = () => this.retryInspirationalThumbnailIfMissing();
    document.addEventListener("visibilitychange", this.visibilityChangeHandler);
    window.addEventListener("focus", this.windowFocusHandler);
  }

  autoCheck(field: string) {
    this.autoCheckEvent.emit(field);
  }

  onFontChange(font: string, fontSelectBox: HTMLElement) {
    this.model.brandFont = font;
    this.renderer.setStyle(fontSelectBox, "font-family", font + "!important");
    this.modelChange.emit(this.model);
  }

  async uploadBrandLogoFile(e: File[]) {
    if (!this.isFileSizeValid(e[0])) {
      this.toastr.error("Max file size exceeds 50MB", "Error");
      return;
    }
    this.brandLogoFile = e[0];
    if (this.isFileTypeValid(this.brandLogoFile)) {
      this.brandLogoFile = await this.heicConverter.convertToJpegIfNeeded(this.brandLogoFile);
      const uploadKey = this.generateUploadKey("brand_logo", this.brandLogoFile.name);
      this.setUploadState(uploadKey, true);
      this.brandLogoUploadError = null;
      this.model.brandLogoUploadKey = uploadKey;
      this.model.brandLogo = null;
      const startBrandLogoUpload = (attempt: number) => {
        const brandLogoSubscription = this.uploadService.uploadFileToS3("digital/photos", this.brandLogoFile).subscribe(
          (response: any) => {
            console.log(response);
            if (response && response.type === "progress") {
              this.setUploadProgress(uploadKey, response.percent);
              return;
            }

            if (response && response.type === "complete") {
              this.setUploadState(uploadKey, false);
              this.model.brandLogo = response.url;
              this.brandLogoUploadError = null;
              this.failedBrandLogoFile = null;
              this.toastr.success(`Brand Logo ${this.brandLogoFile.name} uploaded successfully`);
              this.model.brandLogoUploadKey = null;
              this.cleanupUpload(uploadKey);
              this.activeUploads.delete(uploadKey);
              this.modelChange.emit(this.model);
            }
          },
          error => {
            if (attempt < this.FILE_UPLOAD_MAX_RETRIES) {
              setTimeout(() => startBrandLogoUpload(attempt + 1), this.FILE_UPLOAD_RETRY_DELAY_MS);
              return;
            }
            this.setUploadState(uploadKey, false);
            this.brandLogoUploadError = this.getReadableUploadError(error);
            this.failedBrandLogoFile = this.brandLogoFile;
            console.error("Upload error:", error);
            this.model.brandLogoUploadKey = null;
            this.cleanupUpload(uploadKey);
            this.activeUploads.delete(uploadKey);
            this.modelChange.emit(this.model);
          }
        );
        this.activeUploads.set(uploadKey, { subscription: brandLogoSubscription });
      };
      startBrandLogoUpload(1);
    } else {
      this.toastr.error("Invalid file type");
    }
  }

  removeBrandLogo() {
    this.model.brandLogo = null;
    this.brandLogoUploadError = null;
    this.failedBrandLogoFile = null;
    this.modelChange.emit(this.model);
  }

  uploadInspirationalVideoFile(f: File[]) {
    this.inspirationalVideoFile = f[0];
    if (this.inspirationalVideoFile && this.inspirationalVideoFile.type.startsWith("video/")) {
      const uploadKey = this.generateUploadKey("inspirational_video", this.inspirationalVideoFile.name);
      this.setUploadState(uploadKey, true);
      this.inspirationalVideoUploadError = null;
      this.model.inspirationalVideoUploadKey = uploadKey;
      let localThumbnailFailed = false;
      // Không set inspirationalVideo = null ở đây để tránh mất dữ liệu khi đang upload
      // Chỉ set khi upload thực sự bắt đầu và có thể theo dõi qua uploadKey

      // Track thumbnail upload subscription
      const thumbnailKey = `${uploadKey}_thumbnail`;
      const thumbnailSubscription = this.uploadThumbnailService
        .generateThumbnailFromVideo(this.inspirationalVideoFile)
        .subscribe(
          (response: any) => {
            if (response && response.type === "complete") {
              this.model.inspirationalVideoThumbnail = response.url;
              // Emit chỉ thumbnail, không emit inspirationalVideo để tránh ghi đè
              // Chỉ emit field thumbnail, không emit inspirationalVideo nếu chưa có giá trị hợp lệ
              const updateData: any = {
                inspirationalVideoThumbnail: response.url,
              };
              // Chỉ thêm inspirationalVideo nếu đã có giá trị hợp lệ
              if (
                this.model.inspirationalVideo &&
                typeof this.model.inspirationalVideo === "string" &&
                this.model.inspirationalVideo.trim() !== ""
              ) {
                updateData.inspirationalVideo = this.model.inspirationalVideo;
              }
              this.modelChange.emit(updateData);
              this.activeUploads.delete(thumbnailKey);
            }
          },
          error => {
            console.error("Thumbnail generation failed:", error);
            // Thumbnail is optional for inspirational video; continue upload silently.
            localThumbnailFailed = true;
            this.activeUploads.delete(thumbnailKey);
          }
        );
      this.activeUploads.set(thumbnailKey, { subscription: thumbnailSubscription });

      const startInspirationalVideoUpload = (attempt: number) => {
        const videoSubscription = this.multipartUploadService
          .uploadVideoMultipart("digital/videos", this.inspirationalVideoFile, {
            maxSizeMB: 5120,
            chunkSizeMB: 15,
            fileId: uploadKey,
          })
          .subscribe(
            (response: any) => {
              console.log(response);
              if (response && response.type === "progress") {
                this.setUploadProgress(uploadKey, response.percent);
                return;
              }

              if (response && response.type === "complete") {
                this.setUploadState(uploadKey, false);
                // Đảm bảo cập nhật đúng giá trị và emit với toàn bộ model
                const updatedModel = {
                  ...this.model,
                  inspirationalVideo: response.url,
                  inspirationalVideoUploadKey: null,
                };
                this.model.inspirationalVideo = updatedModel.inspirationalVideo;
                this.model.inspirationalVideoUploadKey = null;
                this.autoCheck("displayInspirationalVideo");
                this.cleanupUpload(uploadKey);
                this.activeUploads.delete(uploadKey);
                // Emit với toàn bộ model để đảm bảo sync đúng
                this.modelChange.emit(updatedModel);
                if (
                  localThumbnailFailed ||
                  !this.hasDisplayableInspirationalThumbnail(this.model.inspirationalVideoThumbnail)
                ) {
                  this.ensureInspirationalThumbnailFromUploadedUrl(
                    response.url,
                    this.inspirationalVideoFile && this.inspirationalVideoFile.name
                      ? this.inspirationalVideoFile.name
                      : "video",
                    uploadKey
                  );
                }
              }
            },
            error => {
              // Ignore error if upload was cancelled
              if (error.message && error.message.includes("cancelled")) {
                this.activeUploads.delete(uploadKey);
                return;
              }

              if (attempt < this.VIDEO_UPLOAD_MAX_RETRIES) {
                setTimeout(() => startInspirationalVideoUpload(attempt + 1), this.VIDEO_UPLOAD_RETRY_DELAY_MS);
                return;
              }

              this.setUploadState(uploadKey, false);
              this.model.inspirationalVideoUploadKey = null;
              this.inspirationalVideoUploadError = this.getReadableUploadError(error);
              this.failedInspirationalVideoFile = this.inspirationalVideoFile;
              console.error("Upload error:", error);
              this.cleanupUpload(uploadKey);
              this.activeUploads.delete(uploadKey);
              // Emit để cập nhật trạng thái lỗi
              this.modelChange.emit({ ...this.model });
            }
          );
        this.activeUploads.set(uploadKey, { subscription: videoSubscription });
      };

      startInspirationalVideoUpload(1);
    }
  }

  removeInspirationalVideo() {
    // Cancel uploads if in progress
    const uploadKey = this.model.inspirationalVideoUploadKey;
    if (uploadKey) {
      // Cancel video upload
      const videoUpload = this.activeUploads.get(uploadKey);
      if (videoUpload && videoUpload.subscription && !videoUpload.subscription.closed) {
        videoUpload.subscription.unsubscribe();
        this.activeUploads.delete(uploadKey);
      }

      // Cancel thumbnail upload
      const thumbnailKey = `${uploadKey}_thumbnail`;
      const thumbnailUpload = this.activeUploads.get(thumbnailKey);
      if (thumbnailUpload && thumbnailUpload.subscription && !thumbnailUpload.subscription.closed) {
        thumbnailUpload.subscription.unsubscribe();
        this.activeUploads.delete(thumbnailKey);
      }

      // Cancel thumbnail-from-url upload
      const thumbnailFromUrlKey = `${uploadKey}_thumbnail_url`;
      const thumbnailFromUrlUpload = this.activeUploads.get(thumbnailFromUrlKey);
      if (
        thumbnailFromUrlUpload &&
        thumbnailFromUrlUpload.subscription &&
        !thumbnailFromUrlUpload.subscription.closed
      ) {
        thumbnailFromUrlUpload.subscription.unsubscribe();
        this.activeUploads.delete(thumbnailFromUrlKey);
      }

      // Cleanup upload state and progress
      this.cleanupUpload(uploadKey);
      this.model.inspirationalVideoUploadKey = null;
    }

    // Clear video data
    this.model.inspirationalVideo = null;
    this.model.inspirationalVideoThumbnail = null;
    this.inspirationalVideoUploadError = null;
    this.failedInspirationalVideoFile = null;
    this.modelChange.emit(this.model);
  }

  async uploadBannerFile(files: File[]) {
    if (!files || files.length === 0) {
      return;
    }
    let file = files[0];

    // Validate file type
    if (!this.isFileTypeValid(file)) {
      this.toastr.error("Invalid file type", "Error");
      return;
    }

    // Validate file size
    if (!this.isFileSizeValid(file)) {
      this.toastr.error("Max file size exceeds 50MB", "Error");
      return;
    }

    file = await this.heicConverter.convertToJpegIfNeeded(file);

    const uploadKey = this.generateUploadKey("banner", file.name);
    this.setUploadState(uploadKey, true);
    this.bannerUploadError = null;
    this.model.bannerUploadKey = uploadKey;
    const startBannerUpload = (attempt: number) => {
      const bannerSubscription = this.uploadService.uploadFileToS3("digital/photos", file).subscribe(
        (response: any) => {
          console.log(response);
          if (response && response.type === "progress") {
            this.setUploadProgress(uploadKey, response.percent);
            return;
          }

          if (response && response.type === "complete") {
            this.setUploadState(uploadKey, false);
            this.model.banner = response.url;
            this.bannerUploadError = null;
            this.failedBannerFile = null;
            this.toastr.success("Banner uploaded successfully");
            this.model.bannerUploadKey = null;
            this.cleanupUpload(uploadKey);
            this.activeUploads.delete(uploadKey);
            this.modelChange.emit(this.model);
          }
        },
        error => {
          if (attempt < this.FILE_UPLOAD_MAX_RETRIES) {
            setTimeout(() => startBannerUpload(attempt + 1), this.FILE_UPLOAD_RETRY_DELAY_MS);
            return;
          }
          this.setUploadState(uploadKey, false);
          this.bannerUploadError = this.getReadableUploadError(error);
          this.failedBannerFile = file;
          console.error("Upload error:", error);
          this.model.bannerUploadKey = null;
          this.cleanupUpload(uploadKey);
          this.activeUploads.delete(uploadKey);
          this.modelChange.emit(this.model);
        }
      );
      this.activeUploads.set(uploadKey, { subscription: bannerSubscription });
    };
    startBannerUpload(1);
  }

  removeBanner(type?: string) {
    this.model.banner = null;
    this.bannerUploadError = null;
    this.failedBannerFile = null;
    // Clear the file input
    const fileInput = document.getElementById("changeBanner") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
    this.modelChange.emit(this.model);
  }

  // Helper methods
  private isFileSizeValid(file: File): boolean {
    const maxSize = 50 * 1024 * 1024; // 50MB
    return file.size <= maxSize;
  }

  private isFileTypeValid(file: File): boolean {
    const validImageTypes = ["image/gif", "image/jpeg", "image/png", "image/heic", "image/heif"];
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    return (
      validImageTypes.includes(file.type) ||
      (isHeicByExtension && (!file.type || file.type === "application/octet-stream"))
    );
  }

  private generateUploadKey(prefix: string, fileName: string): string {
    return `${prefix}_${Date.now()}_${fileName}`;
  }

  private setUploadState(key: string, isUploading: boolean) {
    this.uploadStates[key] = isUploading;
  }

  private setUploadProgress(key: string, percent: number) {
    this.uploadProgress[key] = percent;
  }

  isUploadInProgress(key: string): boolean {
    return this.uploadStates[key] || false;
  }

  getProgressByKey(key: string): number {
    return this.uploadProgress[key] || 0;
  }

  private isInspirationalVideoUploading(): boolean {
    if (!this.model || !this.model.inspirationalVideoUploadKey) {
      return false;
    }

    const uploadKey = this.model.inspirationalVideoUploadKey;
    const isUploading = this.uploadStates[uploadKey] === true;
    const progress = this.uploadProgress[uploadKey];
    const isProgressing = progress !== undefined && progress > 0 && progress < 100;

    return isUploading || isProgressing;
  }

  isInspirationalVideoRequiredError(): boolean {
    return (
      !!this.model.displayInspirationalVideo && !this.model.inspirationalVideo && !this.isInspirationalVideoUploading()
    );
  }

  private cleanupUpload(key: string) {
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  private hasDisplayableInspirationalThumbnail(thumbnail: any): boolean {
    if (typeof thumbnail !== "string") {
      return false;
    }
    const normalized = thumbnail.trim();
    return normalized !== "" && normalized !== "data:,";
  }

  private isBlobUrl(url: string | null | undefined): boolean {
    if (!url) {
      return false;
    }
    return String(url).startsWith("blob:");
  }

  private ensureInspirationalThumbnailFromUploadedUrl(
    videoUrl: string,
    fileName: string,
    dedupeId: string,
    attempt: number = 1
  ): void {
    if (!videoUrl || this.isBlobUrl(videoUrl)) {
      return;
    }
    const dedupeKey = `${dedupeId}_thumbnail_url`;
    if (this.thumbnailFromUrlInFlight.has(dedupeKey)) {
      return;
    }
    this.thumbnailFromUrlInFlight.add(dedupeKey);

    const complete = () => {
      this.thumbnailFromUrlInFlight.delete(dedupeKey);
      this.activeUploads.delete(dedupeKey);
    };

    const subscription = this.uploadThumbnailService
      .generateThumbnailFromVideoUrl(videoUrl, "digital/thumb", 5)
      .subscribe(
        (response: any) => {
          if (!response || response.type !== "complete") {
            return;
          }
          const nextThumbnail =
            typeof response.url === "string" && response.url.trim() !== ""
              ? response.url
              : typeof response.s3Url === "string" && response.s3Url.trim() !== ""
                ? response.s3Url
                : null;

          if (nextThumbnail) {
            this.model.inspirationalVideoThumbnail = nextThumbnail;
            this.modelChange.emit({
              inspirationalVideo: this.model.inspirationalVideo,
              inspirationalVideoThumbnail: nextThumbnail,
            });
            complete();
            return;
          }

          complete();
        },
        () => {
          complete();
          if (attempt < 3) {
            const delayMs = attempt === 1 ? 1200 : 2200;
            setTimeout(() => {
              this.ensureInspirationalThumbnailFromUploadedUrl(videoUrl, fileName, dedupeId, attempt + 1);
            }, delayMs);
            return;
          }
          console.error(`Could not generate thumbnail from uploaded URL for ${fileName}`);
        },
        () => {
          complete();
        }
      );

    this.activeUploads.set(dedupeKey, { subscription });
  }

  private retryInspirationalThumbnailIfMissing(): void {
    if (!this.model || !this.model.inspirationalVideo) {
      return;
    }
    if (this.model.inspirationalVideoUploadKey) {
      return;
    }
    if (this.hasDisplayableInspirationalThumbnail(this.model.inspirationalVideoThumbnail)) {
      return;
    }
    this.ensureInspirationalThumbnailFromUploadedUrl(
      this.model.inspirationalVideo,
      this.inspirationalVideoFile && this.inspirationalVideoFile.name ? this.inspirationalVideoFile.name : "video",
      "inspirational_video_retry"
    );
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  private getReadableUploadError(error: any): string {
    if (!error) return "Unknown upload error";
    if (typeof error.message === "string" && error.message.trim() !== "") {
      return error.message;
    }
    if (typeof error.statusText === "string" && error.statusText.trim() !== "") {
      return error.statusText;
    }
    if (typeof error.type === "string" && error.type.trim() !== "") {
      return error.type;
    }
    if (error instanceof ProgressEvent) {
      return "Network error";
    }
    return "Upload failed";
  }

  ngOnDestroy(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener("visibilitychange", this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    if (this.windowFocusHandler) {
      window.removeEventListener("focus", this.windowFocusHandler);
      this.windowFocusHandler = null;
    }
    this.activeUploads.forEach((upload, uploadKey) => {
      if (upload && upload.subscription && !upload.subscription.closed) {
        upload.subscription.unsubscribe();
      }
      this.cleanupUpload(uploadKey);
    });
    this.activeUploads.clear();
    this.thumbnailFromUrlInFlight.clear();
  }

  retryBrandLogoUpload(): void {
    if (!this.failedBrandLogoFile) {
      this.toastr.error("Cannot retry brand logo upload. Please select file again.");
      return;
    }
    this.uploadBrandLogoFile([this.failedBrandLogoFile]);
  }

  retryBannerUpload(): void {
    if (!this.failedBannerFile) {
      this.toastr.error("Cannot retry banner upload. Please select file again.");
      return;
    }
    this.uploadBannerFile([this.failedBannerFile]);
  }

  retryInspirationalVideoUpload(): void {
    if (!this.failedInspirationalVideoFile) {
      this.toastr.error("Cannot retry video upload. Please select file again.");
      return;
    }
    this.uploadInspirationalVideoFile([this.failedInspirationalVideoFile]);
  }
}
